import type {ReactElement} from 'react';
import React from 'react';
import {PanelFooter, PanelFooterPrimaryDefaultAction, PanelFooterSecondaryCustomAction} from '@Components/panel';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Type} from '@Components/button';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import {
  COLLECTION_MEDIA_GRID_ID,
  UploadsMediaSubTab,
  UploadsMediaType,
  USER_UPLOADS_MEDIA_SUB_TABS,
  USER_UPLOADS_MEDIA_TABS,
  USER_UPLOADS_SELECTION_GRID_ID,
} from '@Panels/user-media-panel/user-media-panel.types';
import {selectTab} from '@Components/tabs/tabs-reducer';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {deleteGridItems, getSelectedGridItems} from '@Components/base-grid';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import {openMessageModal} from '@Modals/message-modal';
import {GridUserMediaItemStorage} from '@Libraries/user-media-library';
import {UserMediaType, removeUserItem} from '@Libraries/user-media-library';
import {useSelectedTab} from '@Components/tabs';
import {noop} from '@Utils/general.util';
import {openMoveMultimediaModal} from '@Modals/move-multimedia-modal';
import {UserImageSource} from '@Libraries/user-image-library';
import {capitalize} from '@Utils/string.util';
import {getGridIdForTabStates} from '@Panels/user-media-panel/user-media.library';
import {useAppDispatch, useAppSelector} from '@/hooks';

interface MediaPanelFooterProps {
  isSmallScreen?: boolean;
  defaultTab: UploadsMediaType;
  bottomPills?: Array<PanelBottomPill>;
  mediaTypesToShow: Array<UploadsMediaType>;
  onAddSelectedCTAText?: string;
  hideSecondaryActions?: boolean;

  onClose(): void;

  onAddSelected(): void;
}

export function MediaPanelFooter({
  isSmallScreen = false,
  onClose = noop,
  onAddSelected = noop,
  bottomPills = [],
  mediaTypesToShow = [],
  defaultTab = UploadsMediaType.IMAGE,
  hideSecondaryActions = false,
  ...props
}: MediaPanelFooterProps): ReactElement | null {
  const currentCollectionId = useAppSelector((state) => {
    return state.mediaUploads.collection;
  });
  const selectedGridItems = useAppSelector((state) => {
    return getSelectedGridItems(state, USER_UPLOADS_SELECTION_GRID_ID) as Array<GridUserMediaItemStorage>;
  });
  const activeTab = useSelectedTab(USER_UPLOADS_MEDIA_TABS, defaultTab) as UploadsMediaType;
  const activeSubtab = useSelectedTab(USER_UPLOADS_MEDIA_SUB_TABS, UploadsMediaSubTab.UPLOADS) as UploadsMediaSubTab;
  const dispatch = useAppDispatch();

  const MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE = {
    [UploadsMediaType.IMAGE]: UserMediaType.IMAGE,
    [UploadsMediaType.IMAGE_BACKGROUND]: UserMediaType.IMAGE_BACKGROUND,
    [UploadsMediaType.VIDEO]: UserMediaType.VIDEO,
    [UploadsMediaType.AUDIO]: UserMediaType.AUDIO,
  };

  const onMoveMultimedia = (): void => {
    openMoveMultimediaModal({
      multimediaType: MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE[activeTab],
      currentCollectionId,
      selectedItems: selectedGridItems,
      onMoveItems: onItemsMoved,
    });
  };

  const onItemsMoved = (items: GridItemStorage[]): void => {
    const toRemoveIds: Array<string> = [];
    for (const gridItem of items) {
      toRemoveIds.push(gridItem.id);
    }
    dispatch(
      deleteGridItems({
        gridId: currentCollectionId ? COLLECTION_MEDIA_GRID_ID : getGridIdForTabStates(activeTab, activeSubtab),
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
        itemIdsToDelete: toRemoveIds,
      })
    );
  };

  const removeMedia = async (): Promise<void> => {
    let failed = 0;
    const toRemoveIds: Array<string> = [];
    const userID = getUserId();
    if (!userID) {
      return;
    }
    const processed = selectedGridItems.map(async (item) => {
      try {
        if (await removeSingleUserImage(userID, item)) {
          toRemoveIds.push(item.id);
        }
      } catch (e) {
        console.error(e);
        failed += 1;
      }
    });

    await Promise.all(processed);
    dispatch(
      deleteGridItems({
        gridId: currentCollectionId ? COLLECTION_MEDIA_GRID_ID : getGridIdForTabStates(activeTab, activeSubtab),
        gridSelectionGroupId: USER_UPLOADS_SELECTION_GRID_ID,
        itemIdsToDelete: toRemoveIds,
      })
    );

    if (failed) {
      onRemoveImageError(failed);
    }
  };

  const onRemoveImageError = (numFailed: number): void => {
    const errorLangKey = numFailed === 1 ? 'pmwjs_remove_image_error' : 'pmwjs_remove_image_errors';
    const message = window.i18next.t(errorLangKey, {
      num: numFailed,
    });

    openErrorModal({
      message,
    });
  };

  const removeSingleUserImage = async (userID: number, gridItem: GridUserMediaItemStorage): Promise<boolean> => {
    const isNotOwner = currentCollectionId && userID !== gridItem.uploaderId;
    if (isNotOwner) {
      openMessageModal({
        title: window.i18next.t('pmwjs_can_not_remove_image_title'),
        text: window.i18next.t('pmwjs_can_not_remove_image'),
      });
      return false;
    }

    await removeUserItem(gridItem.hashedFilename, gridItem.uploaderId, MAP_UPLOADS_MEDIA_TYPE_TO_MEDIA_TYPE[activeTab]);
    return true;
  };

  const getFooterSecondaryActions = (): Array<ReactElement> => {
    if (!selectedGridItems.length || !isUserLoggedIn() || hideSecondaryActions) {
      return [];
    }

    const btns: Array<ReactElement> = [];

    if (isSmallScreen) {
      if (activeTab !== UploadsMediaType.IMAGE_BACKGROUND) {
        btns.push(
          <PanelFooterSecondaryCustomAction key="btn-move-selected">
            <div className="flex-center">
              <Icon
                icon="icon-move-collection"
                size={IconSize.SIZE_ICON_20}
                shape={IconShape.SQUARE}
                type={IconType.GHOST}
                isDisabled={disableSecondaryActionButtons()}
                onClick={onMoveMultimedia}
              />
            </div>
          </PanelFooterSecondaryCustomAction>
        );
      }
      btns.push(
        <PanelFooterSecondaryCustomAction key="btn-remove-selected">
          <div className="flex-center">
            <Icon
              icon="icon-delete"
              className={disableSecondaryActionButtons() ? '' : 'color-danger-100'}
              size={IconSize.SIZE_ICON_20}
              shape={IconShape.SQUARE}
              type={IconType.GHOST_UNCOLORED}
              onClick={(): void => {
                openConfirmDeleteModal({
                  text: window.i18next.t('pmwjs_remove_user_upload_item'),
                  showBtnDeletingState: true,
                  onDeleteConfirmation: removeMedia,
                });
              }}
              isDisabled={disableSecondaryActionButtons()}
            />
          </div>
        </PanelFooterSecondaryCustomAction>
      );
      return btns;
    }
    if (activeTab !== UploadsMediaType.IMAGE_BACKGROUND) {
      btns.push(
        <PanelFooterPrimaryDefaultAction
          icon="icon-move-collection"
          key="btn-move-selected"
          onClick={onMoveMultimedia}
          disabled={disableSecondaryActionButtons()}
          text={window.i18next.t('pmwjs_move')}
          type={Type.SECONDARY}
        />
      );
    }
    btns.push(
      <PanelFooterPrimaryDefaultAction
        icon="icon-delete"
        key="btn-remove-selected"
        className="spacing-m-l-2"
        iconClassName={disableSecondaryActionButtons() ? '' : 'color-danger-100'}
        type={Type.SECONDARY}
        onClick={(): void => {
          openConfirmDeleteModal({
            text: window.i18next.t('pmwjs_remove_user_upload_item'),
            showBtnDeletingState: true,
            onDeleteConfirmation: removeMedia,
          });
        }}
        disabled={disableSecondaryActionButtons()}
        text={window.i18next.t('pmwjs_delete')}
      />
    );
    return btns;
  };

  const getFooterPrimaryActions = (): Array<ReactElement> => {
    if (!selectedGridItems.length) {
      return [];
    }
    return [
      <PanelFooterPrimaryDefaultAction
        key="btn-add-selected"
        text={props.onAddSelectedCTAText ?? window.i18next.t('pmwjs_add_selected')}
        onClick={(): void => {
          onClose();
          onAddSelected();
        }}
      />,
    ];
  };

  const getFooterBottomPills = (): Array<PanelBottomPill> => {
    if (bottomPills?.length) {
      return bottomPills;
    }
    if (!isSmallScreen) {
      return [];
    }
    const pills = [];
    if (mediaTypesToShow.includes(UploadsMediaType.IMAGE)) {
      pills.push({
        id: UploadsMediaType.IMAGE,
        text: capitalize(window.i18next.t('pmwjs_photos')),
        icon: 'icon-image',
        isSelected: activeTab === UploadsMediaType.IMAGE,
        onClick: (): void => {
          dispatch(selectTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.IMAGE));
        },
      });
    }
    if (mediaTypesToShow.includes(UploadsMediaType.VIDEO)) {
      pills.push({
        id: UploadsMediaType.VIDEO,
        text: capitalize(window.i18next.t('pmwjs_videos')),
        icon: 'icon-videocam',
        isSelected: activeTab === UploadsMediaType.VIDEO,
        onClick: (): void => {
          dispatch(selectTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.VIDEO));
        },
      });
    }
    if (mediaTypesToShow.includes(UploadsMediaType.AUDIO)) {
      pills.push({
        id: UploadsMediaType.AUDIO,
        text: capitalize(window.i18next.t('pmwjs_audio')),
        icon: 'icon-audio',
        isSelected: activeTab === UploadsMediaType.AUDIO,
        onClick: (): void => {
          dispatch(selectTab(USER_UPLOADS_MEDIA_TABS, UploadsMediaType.AUDIO));
        },
      });
    }
    return pills.length > 1 ? pills : [];
  };

  const disableSecondaryActionButtons = (): boolean => {
    for (const item of selectedGridItems) {
      if (item.source === UserImageSource.BRAND_LOGOS) {
        return true;
      }
    }
    return false;
  };

  if (!selectedGridItems.length && !isSmallScreen && !bottomPills?.length) {
    return null;
  }

  return (
    <PanelFooter
      primaryActions={getFooterPrimaryActions()}
      secondaryActions={getFooterSecondaryActions()}
      bottomPills={getFooterBottomPills()}
      fullWidthPrimaryActions={isSmallScreen && hideSecondaryActions}
    />
  );
}
